import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { printElementById } from '../utils/printDocument';
import {
  companyAccountingApi,
  type CompanyAccountingEntry,
  type CompanyBuyerPrincipalLiability,
  type CompanyAccountingLedgerRow,
  type CompanyAccountingPaymentRow,
  type CompanyAccountingPayoutRow,
  type CompanyAccountingRefundRow,
  type CompanyRotReconciliationRow,
  type CompanyAccountingReport as CompanyAccountingReportData
} from '../services/api';

const formatOre = (ore: number | null | undefined, currency: string | null | undefined) => {
  const amount = (ore ?? 0) / 100;
  const currencyCode = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
  if (/^[A-Z]{3}$/.test(currencyCode)) {
    try {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch {
      // Fall through to an unlabelled amount when the code is not supported.
    }
  }
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const formatDate = (value: string | null | undefined) =>
  value ? new Intl.DateTimeFormat('sv-SE').format(new Date(value)) : '—';

const dispositionLabelKeys: Record<string, string> = {
  seller_fee: 'companyAccountingReport.dispositions.seller_fee',
  milestone_completion: 'companyAccountingReport.dispositions.milestone_completion',
  refund: 'companyAccountingReport.dispositions.refund',
  other: 'companyAccountingReport.dispositions.other'
};

const accountingEntryLabelKeys: Record<string, string> = {
  BUYER_PRINCIPAL_LIABILITY: 'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_LIABILITY',
  BUYER_PRINCIPAL_MILESTONE_COMPLETION: 'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_MILESTONE_COMPLETION',
  BUYER_PRINCIPAL_MILESTONE_COMPLETION_VAT: 'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_MILESTONE_COMPLETION_VAT',
  BUYER_PRINCIPAL_MILESTONE_COMPLETION_REVERSAL:
    'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_MILESTONE_COMPLETION_REVERSAL',
  BUYER_PRINCIPAL_MILESTONE_COMPLETION_REVERSAL_VAT:
    'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_MILESTONE_COMPLETION_REVERSAL_VAT',
  SELLER_PLATFORM_FEE: 'companyAccountingReport.entryTypes.SELLER_PLATFORM_FEE',
  SELLER_PLATFORM_FEE_VAT: 'companyAccountingReport.entryTypes.SELLER_PLATFORM_FEE_VAT',
  SELLER_PAYOUT: 'companyAccountingReport.entryTypes.SELLER_PAYOUT',
  SELLER_RETENTION_PAYOUT: 'companyAccountingReport.entryTypes.SELLER_RETENTION_PAYOUT',
  BUYER_PRINCIPAL_REFUND: 'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_REFUND',
  BUYER_REFUND_REVERSAL: 'companyAccountingReport.entryTypes.BUYER_REFUND_REVERSAL',
  BUYER_SELLER_FEE_REFUND: 'companyAccountingReport.entryTypes.BUYER_SELLER_FEE_REFUND',
  BUYER_SELLER_FEE_REFUND_VAT: 'companyAccountingReport.entryTypes.BUYER_SELLER_FEE_REFUND_VAT',
  BUYER_SELLER_FEE_REFUND_REVERSAL: 'companyAccountingReport.entryTypes.BUYER_SELLER_FEE_REFUND_REVERSAL',
  BUYER_SELLER_FEE_REFUND_REVERSAL_VAT: 'companyAccountingReport.entryTypes.BUYER_SELLER_FEE_REFUND_REVERSAL_VAT',
  BUYER_PRINCIPAL_OTHER: 'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_OTHER',
  BUYER_PRINCIPAL_OTHER_REVERSAL: 'companyAccountingReport.entryTypes.BUYER_PRINCIPAL_OTHER_REVERSAL'
};

const accountingEntryLabel = (t: TFunction, entryType: string) =>
  (accountingEntryLabelKeys[entryType] ? t(accountingEntryLabelKeys[entryType]) : '') ||
  entryType
    .toLocaleLowerCase('sv-SE')
    .replaceAll('_', ' ')
    .replace(/^./, (letter) => letter.toLocaleUpperCase('sv-SE'));

const projectReference = (
  t: TFunction,
  row: {
    projectNumber: number | null;
    projectDescription: string | null;
  }
) =>
  row.projectNumber != null
    ? row.projectDescription
      ? t('companyAccountingReport.references.projectWithDescription', {
          number: row.projectNumber,
          description: row.projectDescription
        })
      : t('companyAccountingReport.references.project', { number: row.projectNumber })
    : row.projectDescription || t('companyAccountingReport.references.projectMissing');

const contractReference = (
  t: TFunction,
  row: {
    contractNumber: number | null;
    contractName: string | null;
  }
) =>
  row.contractNumber != null
    ? row.contractName
      ? t('companyAccountingReport.references.contractWithDescription', {
          number: row.contractNumber,
          description: row.contractName
        })
      : t('companyAccountingReport.references.contract', { number: row.contractNumber })
    : row.contractName || t('companyAccountingReport.references.contractMissing');

const milestoneReference = (
  t: TFunction,
  row: {
    milestoneNumber: number | null;
    milestoneDescription: string | null;
  }
) =>
  row.milestoneNumber != null
    ? row.milestoneDescription
      ? t('companyAccountingReport.references.milestoneWithDescription', {
          number: row.milestoneNumber,
          description: row.milestoneDescription
        })
      : t('companyAccountingReport.references.milestone', { number: row.milestoneNumber })
    : row.milestoneDescription || null;

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const monthStartIsoDate = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
};

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function SummaryCard({ label, value, tone = 'primary' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="col-sm-6 col-xl-3">
      <Card className="h-100">
        <div className="text-muted small mb-1">{label}</div>
        <div className={`fs-5 fw-bold text-${tone}`}>{value}</div>
      </Card>
    </div>
  );
}

type Voucher = {
  kind: 'accounting' | 'payment' | 'refund' | 'refund_reversal' | 'payout' | 'ledger';
  reference: string;
  date: string | null;
  project: string;
  contract?: string | null;
  milestone?: string | null;
  customer: string;
  company: string;
  currency: string | null;
  amountOre: number;
  accountingEntries?: CompanyAccountingEntry[];
  lines: Array<{
    label: string;
    value: string | number | null | undefined;
    amountOre?: number;
    semantic?: 'cost' | 'position';
  }>;
};

const textValue = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

const entrySruCode = (row: CompanyAccountingEntry) => {
  const codes = [
    row.debitSruCode ? `D ${row.debitSruCode}` : null,
    row.creditSruCode ? `K ${row.creditSruCode}` : null
  ].filter(Boolean);
  return codes.length ? codes.join(' · ') : '—';
};

const voucherTitleKeys: Record<Voucher['kind'], string> = {
  accounting: 'companyAccountingReport.voucherTitles.accounting',
  payment: 'companyAccountingReport.voucherTitles.payment',
  refund: 'companyAccountingReport.voucherTitles.refund',
  refund_reversal: 'companyAccountingReport.voucherTitles.refund_reversal',
  payout: 'companyAccountingReport.voucherTitles.payout',
  ledger: 'companyAccountingReport.voucherTitles.ledger'
};

const amountTone = (line: Voucher['lines'][number]) => {
  if (!line.amountOre) return '';
  if (line.semantic === 'cost') return line.amountOre < 0 ? 'text-success' : 'text-danger';
  return line.amountOre < 0 ? 'text-danger' : 'text-success';
};

function AccountingEntryTable({
  rows,
  currency,
  onVoucher,
  groupByProject
}: {
  rows: CompanyAccountingEntry[];
  currency: string | null;
  onVoucher: (row: CompanyAccountingEntry) => void;
  groupByProject: boolean;
}) {
  const { t } = useTranslation();
  const renderRow = (row: CompanyAccountingEntry) => (
    <tr key={row.id}>
      <td>
        <button
          type="button"
          className="btn btn-link btn-sm p-0 font-monospace text-decoration-none"
          onClick={() => onVoucher(row)}
        >
          {row.verificationNumber}
        </button>
      </td>
      <td className="text-nowrap">{formatDate(row.bookedAt)}</td>
      <td>
        <div className="fw-semibold">{accountingEntryLabel(t, row.entryType)}</div>
        <div className="small text-muted font-monospace">{row.entryType}</div>
      </td>
      <td style={{ minWidth: 240 }}>
        <div className="fw-semibold">{projectReference(t, row)}</div>
        <div className="small text-muted">{contractReference(t, row)}</div>
        {milestoneReference(t, row) && <div className="small text-primary">{milestoneReference(t, row)}</div>}
        {row.customerName && (
          <div className="small text-muted">
            {t('companyAccountingReport.references.customer', { name: row.customerName })}
          </div>
        )}
      </td>
      <td>
        <span className="font-monospace">{row.debitAccount}</span>
        <span className="small text-muted d-block">{row.debitAccountName}</span>
      </td>
      <td>
        <span className="font-monospace">{row.creditAccount}</span>
        <span className="small text-muted d-block">{row.creditAccountName}</span>
      </td>
      <td className="text-end fw-semibold text-nowrap">{formatOre(row.amountOre, currency)}</td>
      <td className="text-end text-nowrap">{row.vatOre != null ? formatOre(row.vatOre, currency) : '—'}</td>
      <td className="font-monospace small text-nowrap">{entrySruCode(row)}</td>
      <td className="small font-monospace text-break">{row.adyenReference || row.sourceEventKey || '—'}</td>
    </tr>
  );

  const groupedRows = [...rows.reduce((groups, row) => {
    const key = row.projectId || '__without_project__';
    const group = groups.get(key) || [];
    group.push(row);
    groups.set(key, group);
    return groups;
  }, new Map<string, CompanyAccountingEntry[]>()).entries()].sort(([, left], [, right]) => {
    const leftNumber = left[0]?.projectNumber ?? Number.MAX_SAFE_INTEGER;
    const rightNumber = right[0]?.projectNumber ?? Number.MAX_SAFE_INTEGER;
    return leftNumber - rightNumber;
  });

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.accountingTable.columns.verification')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.date')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.event')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.projectContractMilestone')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.debit')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.credit')}</th>
            <th className="text-end">{t('companyAccountingReport.accountingTable.columns.amount')}</th>
            <th className="text-end">{t('companyAccountingReport.accountingTable.columns.vat', 'Moms')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.sruCode')}</th>
            <th>{t('companyAccountingReport.accountingTable.columns.reference')}</th>
          </tr>
        </thead>
        <tbody>
          {groupByProject
            ? groupedRows.map(([projectId, projectRows]) => {
                const first = projectRows[0];
                const verificationCount = new Set(projectRows.map((row) => row.verificationNumber)).size;
                return (
                  <React.Fragment key={projectId}>
                    <tr className="table-secondary">
                      <td colSpan={10} className="py-2 px-3">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                          <div>
                            <span className="fw-bold">{projectReference(t, first)}</span>
                            {first.customerName && (
                              <span className="text-muted ms-2">
                                {t('companyAccountingReport.references.customer', { name: first.customerName })}
                              </span>
                            )}
                          </div>
                          <span className="small text-muted text-nowrap">
                            {t('companyAccountingReport.accountingTable.groupSummary', {
                              verifications: verificationCount,
                              rows: projectRows.length
                            })}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {projectRows.map(renderRow)}
                  </React.Fragment>
                );
              })
            : rows.map(renderRow)}
          {!rows.length && (
            <tr>
              <td colSpan={10} className="text-muted py-4 text-center">
                {t('companyAccountingReport.accountingTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BuyerPrincipalLiabilityTable({ rows }: { rows: CompanyBuyerPrincipalLiability[] }) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.liabilityTable.columns.buyerAndOriginalPayment')}</th>
            <th>{t('companyAccountingReport.liabilityTable.columns.project')}</th>
            <th>{t('companyAccountingReport.liabilityTable.columns.paidIn')}</th>
            <th className="text-end">{t('companyAccountingReport.liabilityTable.columns.originalPrincipal')}</th>
            <th className="text-end">{t('companyAccountingReport.liabilityTable.columns.remainingLiability')}</th>
            <th>{t('companyAccountingReport.liabilityTable.columns.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr>
                <td>
                  <div className="fw-semibold">{row.buyer.fullName || row.buyer.email || row.buyerUserId}</div>
                  <div className="small text-muted font-monospace text-break">{row.originalPaymentReference}</div>
                </td>
                <td>
                  {row.projectId ? (
                    <Link to={`/projects/${row.projectId}`}>
                      {t('companyAccountingReport.references.project', { number: row.projectNumber ?? '—' })}
                    </Link>
                  ) : (
                    '—'
                  )}
                  {row.projectDescription && <div className="small text-muted">{row.projectDescription}</div>}
                </td>
                <td>{formatDate(row.receivedAt)}</td>
                <td className="text-end">{formatOre(row.originalPrincipalOre, row.currency)}</td>
                <td className="text-end fw-bold">{formatOre(row.remainingPrincipalOre, row.currency)}</td>
                <td>
                  <span className={`badge ${row.status === 'settled' ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === 'settled'
                      ? t('companyAccountingReport.liabilityTable.statusSettled')
                      : t('companyAccountingReport.liabilityTable.statusOpen')}
                  </span>
                </td>
              </tr>
              <tr className="table-light">
                <td colSpan={6} className="px-4 py-2">
                  <div className="small fw-semibold mb-1">
                    {t('companyAccountingReport.liabilityTable.dispositionsHeading')}
                  </div>
                  {row.dispositions.length === 0 ? (
                    <span className="small text-muted">
                      {t('companyAccountingReport.liabilityTable.noDispositions')}
                    </span>
                  ) : (
                    <div className="d-flex flex-column gap-1">
                      {row.dispositions.map((disposition) => (
                        <div className="small d-flex flex-wrap gap-2" key={disposition.id}>
                          <span>{formatDate(disposition.occurredAt)}</span>
                          <span className="badge bg-white text-dark border">
                            {dispositionLabelKeys[disposition.kind]
                              ? t(dispositionLabelKeys[disposition.kind])
                              : disposition.kind}
                          </span>
                          <span className={disposition.direction === 'decrease' ? 'text-success' : 'text-warning'}>
                            {disposition.direction === 'decrease' ? '−' : '+'}
                            {formatOre(disposition.amountOre, row.currency)}
                          </span>
                          <span>{disposition.description}</span>
                          <span className="text-muted font-monospace">
                            {disposition.sourceReference || disposition.sourceId}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            </React.Fragment>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={6} className="text-muted py-4 text-center">
                {t('companyAccountingReport.liabilityTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaymentTable({
  rows,
  onVoucher
}: {
  rows: CompanyAccountingPaymentRow[];
  onVoucher: (row: CompanyAccountingPaymentRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.paymentTable.columns.date')}</th>
            <th>{t('companyAccountingReport.paymentTable.columns.projectContractMilestone')}</th>
            <th>{t('companyAccountingReport.paymentTable.columns.customer')}</th>
            <th>{t('companyAccountingReport.paymentTable.columns.psp')}</th>
            <th className="text-end">{t('companyAccountingReport.paymentTable.columns.buyerPrincipal')}</th>
            <th className="text-end">{t('companyAccountingReport.paymentTable.columns.etappioFee')}</th>
            <th className="text-end">{t('companyAccountingReport.paymentTable.columns.otherFees')}</th>
            <th className="text-end">{t('companyAccountingReport.paymentTable.columns.onAdyenAccount')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.paymentId}>
              <td>{formatDate(row.date)}</td>
              <td>
                <div className="fw-semibold">{projectReference(t, row)}</div>
                <div className="small text-muted">{contractReference(t, row)}</div>
                {milestoneReference(t, row) && <div className="small text-primary">{milestoneReference(t, row)}</div>}
              </td>
              <td>{row.customerName || '—'}</td>
              <td>
                {row.pspReference ? (
                  <button
                    className="btn btn-link btn-sm p-0 font-monospace text-decoration-none text-break"
                    type="button"
                    onClick={() => onVoucher(row)}
                  >
                    {row.pspReference}
                  </button>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="text-end">{formatOre(row.grossAmountOre, row.currency)}</td>
              <td className="text-end text-danger">{formatOre(row.etappioFeeOre, row.currency)}</td>
              <td className="text-end text-danger">
                {formatOre(row.paymentMethodFeeOre + row.adyenFeeOre, row.currency)}
              </td>
              <td className="text-end fw-semibold">{formatOre(row.netToCompanyOre, row.currency)}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={8} className="text-muted py-4 text-center">
                {t('companyAccountingReport.paymentTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RotReconciliationTable({
  rows,
  currency
}: {
  rows: CompanyRotReconciliationRow[];
  currency: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.rotTable.columns.contractAndMilestone')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.beforeRot')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.plannedRot')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.afterRot')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.principalPaid')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.feeBase')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.etappioFee')}</th>
            <th className="text-end">{t('companyAccountingReport.rotTable.columns.netLiquidity')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.scope}:${row.milestoneId ?? row.contractId}`}
              className={row.scope === 'contract' ? 'table-secondary' : ''}
            >
              <td>
                <div className={row.scope === 'contract' ? 'fw-bold' : 'ps-3'}>
                  {row.scope === 'contract'
                    ? t('companyAccountingReport.references.projectContract', {
                        projectNumber: row.projectNumber ?? '—',
                        contractNumber: row.contractNumber
                      })
                    : t('companyAccountingReport.references.milestone', { number: row.milestoneNumber ?? '—' })}
                </div>
                <div className={`small text-muted ${row.scope === 'milestone' ? 'ps-3' : ''}`}>
                  {row.description || '—'}
                </div>
              </td>
              <td className="text-end">{formatOre(row.grossAmountOre, currency)}</td>
              <td className="text-end">{formatOre(row.plannedRotAmountOre, currency)}</td>
              <td className="text-end fw-semibold">{formatOre(row.afterRotAmountOre, currency)}</td>
              <td className="text-end">{formatOre(row.actualPrincipalPaidOre, currency)}</td>
              <td className="text-end">{formatOre(row.feeBaseOre, currency)}</td>
              <td className="text-end text-danger">{formatOre(row.etappioFeeOre, currency)}</td>
              <td className="text-end fw-semibold">{formatOre(row.netLiquidityOre, currency)}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={8} className="text-muted py-4 text-center">
                {t('companyAccountingReport.rotTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RefundTable({
  rows,
  onVoucher
}: {
  rows: CompanyAccountingRefundRow[];
  onVoucher: (row: CompanyAccountingRefundRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.refundTable.columns.date')}</th>
            <th>{t('companyAccountingReport.refundTable.columns.projectContractMilestone')}</th>
            <th>{t('companyAccountingReport.refundTable.columns.type')}</th>
            <th>{t('companyAccountingReport.refundTable.columns.reference')}</th>
            <th className="text-end">{t('companyAccountingReport.refundTable.columns.principalAdjustment')}</th>
            <th className="text-end">{t('companyAccountingReport.refundTable.columns.feeAdjustment')}</th>
            <th className="text-end">{t('companyAccountingReport.refundTable.columns.onAdyenAccount')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.refundId}>
              <td>{formatDate(row.date)}</td>
              <td>
                <div className="fw-semibold">{projectReference(t, row)}</div>
                <div className="small text-muted">{contractReference(t, row)}</div>
                {milestoneReference(t, row) && <div className="small text-primary">{milestoneReference(t, row)}</div>}
              </td>
              <td>
                {row.kind === 'refund'
                  ? t('companyAccountingReport.refundTable.typeRefund')
                  : t('companyAccountingReport.refundTable.typeReversal')}
              </td>
              <td>
                <button
                  className="btn btn-link btn-sm p-0 font-monospace text-decoration-none text-break"
                  type="button"
                  onClick={() => onVoucher(row)}
                >
                  {row.reference}
                </button>
              </td>
              <td
                className={`text-end fw-semibold ${row.grossRevenueAdjustmentOre < 0 ? 'text-danger' : 'text-success'}`}
              >
                {formatOre(row.grossRevenueAdjustmentOre, row.currency)}
              </td>
              <td
                className={`text-end ${row.totalFeeAdjustmentOre < 0 ? 'text-success' : row.totalFeeAdjustmentOre > 0 ? 'text-danger' : ''}`}
              >
                {formatOre(row.totalFeeAdjustmentOre, row.currency)}
              </td>
              <td
                className={`text-end fw-semibold ${row.companyBalanceAdjustmentOre < 0 ? 'text-danger' : 'text-success'}`}
              >
                {formatOre(row.companyBalanceAdjustmentOre, row.currency)}
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={7} className="text-muted py-4 text-center">
                {t('companyAccountingReport.refundTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PayoutTable({
  rows,
  onVoucher
}: {
  rows: CompanyAccountingPayoutRow[];
  onVoucher: (row: CompanyAccountingPayoutRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.payoutTable.columns.date')}</th>
            <th>{t('companyAccountingReport.payoutTable.columns.projectContractMilestone')}</th>
            <th>{t('companyAccountingReport.payoutTable.columns.customer')}</th>
            <th>{t('companyAccountingReport.payoutTable.columns.transfer')}</th>
            <th>{t('companyAccountingReport.payoutTable.columns.descriptor')}</th>
            <th>{t('companyAccountingReport.payoutTable.columns.status')}</th>
            <th className="text-end">{t('companyAccountingReport.payoutTable.columns.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.payoutId}>
              <td>{formatDate(row.date)}</td>
              <td>
                <div className="fw-semibold">{projectReference(t, row)}</div>
                <div className="small text-muted">{contractReference(t, row)}</div>
                {milestoneReference(t, row) && <div className="small text-primary">{milestoneReference(t, row)}</div>}
              </td>
              <td>{row.customerName || '—'}</td>
              <td>
                <button
                  className="btn btn-link btn-sm p-0 font-monospace text-decoration-none text-break"
                  type="button"
                  onClick={() => onVoucher(row)}
                >
                  {row.transferReference}
                </button>
              </td>
              <td>{row.statementDescriptor || '—'}</td>
              <td>{row.status}</td>
              <td className="text-end fw-semibold">{formatOre(row.amountOre, row.currency)}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={7} className="text-muted py-4 text-center">
                {t('companyAccountingReport.payoutTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function UnknownLedgerTable({
  rows,
  onVoucher
}: {
  rows: CompanyAccountingLedgerRow[];
  onVoucher: (row: CompanyAccountingLedgerRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('companyAccountingReport.ledgerTable.columns.accountingRef')}</th>
            <th>{t('companyAccountingReport.ledgerTable.columns.date')}</th>
            <th>{t('companyAccountingReport.ledgerTable.columns.type')}</th>
            <th>{t('companyAccountingReport.ledgerTable.columns.psp')}</th>
            <th>{t('companyAccountingReport.ledgerTable.columns.transfer')}</th>
            <th className="text-end">{t('companyAccountingReport.ledgerTable.columns.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <button
                  className="btn btn-link btn-sm p-0 font-monospace text-decoration-none text-break"
                  type="button"
                  onClick={() => onVoucher(row)}
                >
                  {row.accountingReference || row.reference || row.id}
                </button>
              </td>
              <td>{formatDate(row.date)}</td>
              <td>{row.classification}</td>
              <td className="font-monospace small text-break">{row.pspReference || '—'}</td>
              <td className="font-monospace small text-break">{row.transferId || '—'}</td>
              <td className={`text-end fw-semibold ${row.amountOre < 0 ? 'text-danger' : 'text-success'}`}>
                {formatOre(row.amountOre, row.currency)}
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={6} className="text-muted py-4 text-center">
                {t('companyAccountingReport.ledgerTable.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function VoucherModal({ voucher, onClose }: { voucher: Voucher; onClose: () => void }) {
  const { t } = useTranslation();
  const amountLines = voucher.lines.filter((line) => line.amountOre != null);
  const referenceLines = voucher.lines.filter((line) => line.amountOre == null);

  return (
    <div className="modal d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(15, 23, 42, 0.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t(voucherTitleKeys[voucher.kind])}</h5>
            <button
              type="button"
              className="btn-close"
              aria-label={t('companyAccountingReport.actions.close')}
              onClick={onClose}
            />
          </div>
          <div className="modal-body" id="company-accounting-voucher-print">
            <h5 className="d-none d-print-block mb-3">{t(voucherTitleKeys[voucher.kind])}</h5>
            <div className="border rounded p-3 mb-3">
              <small className="text-muted">{t('companyAccountingReport.voucher.reference')}</small>
              <div className="font-monospace fw-semibold text-break">{voucher.reference}</div>
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <dl className="mb-0">
                  <dt>{t('companyAccountingReport.voucher.bookingDate')}</dt>
                  <dd>{formatDate(voucher.date)}</dd>
                  <dt>{t('companyAccountingReport.voucher.amount')}</dt>
                  <dd className={voucher.amountOre < 0 ? 'text-danger' : 'text-success'}>
                    {formatOre(voucher.amountOre, voucher.currency)}
                  </dd>
                  <dt>{t('companyAccountingReport.voucher.company')}</dt>
                  <dd>{voucher.company}</dd>
                  <dt>{t('companyAccountingReport.voucher.currency')}</dt>
                  <dd>{textValue(voucher.currency)}</dd>
                </dl>
              </div>
              <div className="col-md-6">
                <dl className="mb-0">
                  <dt>{t('companyAccountingReport.voucher.project')}</dt>
                  <dd>{voucher.project}</dd>
                  <dt>{t('companyAccountingReport.voucher.contract')}</dt>
                  <dd>{textValue(voucher.contract)}</dd>
                  <dt>{t('companyAccountingReport.voucher.milestone')}</dt>
                  <dd>{textValue(voucher.milestone)}</dd>
                  <dt>{t('companyAccountingReport.voucher.customer')}</dt>
                  <dd>{voucher.customer}</dd>
                </dl>
              </div>
            </div>
            {voucher.accountingEntries && voucher.accountingEntries.length > 0 && (
              <>
                <hr />
                <h6 className="fw-bold">{t('companyAccountingReport.voucher.entriesHeading')}</h6>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('companyAccountingReport.voucher.columns.event')}</th>
                        <th>{t('companyAccountingReport.voucher.columns.debit')}</th>
                        <th>{t('companyAccountingReport.voucher.columns.credit')}</th>
                        <th>{t('companyAccountingReport.voucher.columns.sruCode')}</th>
                        <th className="text-end">{t('companyAccountingReport.voucher.columns.amount')}</th>
                        <th className="text-end">{t('companyAccountingReport.voucher.columns.vat', 'Moms')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voucher.accountingEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>
                            <div>{accountingEntryLabel(t, entry.entryType)}</div>
                            <div className="small text-muted">{entry.description}</div>
                          </td>
                          <td>
                            <span className="font-monospace">{entry.debitAccount}</span>
                            <span className="small text-muted d-block">{entry.debitAccountName}</span>
                          </td>
                          <td>
                            <span className="font-monospace">{entry.creditAccount}</span>
                            <span className="small text-muted d-block">{entry.creditAccountName}</span>
                          </td>
                          <td className="font-monospace small text-nowrap">{entrySruCode(entry)}</td>
                          <td className="text-end fw-semibold text-nowrap">
                            {formatOre(entry.amountOre, voucher.currency)}
                          </td>
                          <td className="text-end text-nowrap">
                            {entry.vatOre != null ? formatOre(entry.vatOre, voucher.currency) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {amountLines.length > 0 && (
              <>
                <hr />
                <div className="row g-3">
                  {amountLines.map((line) => (
                    <div className="col-md-4" key={line.label}>
                      <div className="border rounded p-3 h-100">
                        <small className="text-muted">{line.label}</small>
                        <div className={`fw-semibold ${amountTone(line)}`}>
                          {formatOre(line.amountOre, voucher.currency)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {referenceLines.length > 0 && <hr />}
            <dl className="mb-0">
              {referenceLines.map((line) => (
                <React.Fragment key={line.label}>
                  <dt>{line.label}</dt>
                  <dd className="font-monospace text-break">{textValue(line.value)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              {t('companyAccountingReport.actions.close')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() =>
                printElementById('company-accounting-voucher-print', t(voucherTitleKeys[voucher.kind]))
              }
            >
              <i className="feather-printer me-2"></i>
              {t('companyAccountingReport.actions.print')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CompanyAccountingReport() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [fromDate, setFromDate] = useState(monthStartIsoDate());
  const [toDate, setToDate] = useState(todayIsoDate());
  const [report, setReport] = useState<CompanyAccountingReportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [groupAccountingByProject, setGroupAccountingByProject] = useState(true);

  const companyId = searchParams.get('companyId') || undefined;
  const params = useMemo(() => ({ fromDate, toDate, companyId }), [fromDate, toDate, companyId]);

  const loadReport = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setReport(await companyAccountingApi.getReport(params));
    } catch {
      setReport(null);
      setError(t('companyAccountingReport.errors.load'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await companyAccountingApi.exportCsv(params);
      downloadBlob(result.blob, result.filename);
    } catch {
      setError(t('companyAccountingReport.errors.export'));
    } finally {
      setIsExporting(false);
    }
  };

  const companyLabel = report
    ? `${report.company.name}${
        report.company.organizationNumber
          ? t('companyAccountingReport.meta.orgNrSuffix', { number: report.company.organizationNumber })
          : ''
      }`
    : '—';

  const filteredAccountingEntries = useMemo(() => {
    if (!report) return [];
    if (!selectedProjectId) return report.accountingEntries;
    if (selectedProjectId === '__without_project__') {
      return report.accountingEntries.filter((entry) => !entry.projectId);
    }
    return report.accountingEntries.filter((entry) => entry.projectId === selectedProjectId);
  }, [report, selectedProjectId]);

  const openAccountingVoucher = (row: CompanyAccountingEntry) => {
    if (!report) return;
    const verificationRows = report.accountingEntries.filter(
      (entry) => entry.verificationNumber === row.verificationNumber
    );
    const uniqueValues = (values: Array<string | null>) => [...new Set(values.filter((value): value is string => Boolean(value)))];
    setVoucher({
      kind: 'accounting',
      reference: row.verificationNumber,
      date: row.bookedAt,
      project: projectReference(t, row),
      contract: contractReference(t, row),
      milestone: milestoneReference(t, row),
      customer: row.customerName || '—',
      company: companyLabel,
      currency: report.currency,
      amountOre: verificationRows.reduce((sum, entry) => sum + entry.amountOre, 0),
      accountingEntries: verificationRows,
      lines: [
        { label: t('companyAccountingReport.voucherLines.projectId'), value: row.projectId },
        { label: t('companyAccountingReport.voucherLines.externalProjectNumber'), value: row.projectExternalNumber },
        { label: t('companyAccountingReport.voucherLines.contractId'), value: row.contractId },
        { label: t('companyAccountingReport.voucherLines.milestoneId'), value: row.milestoneId },
        {
          label: t('companyAccountingReport.voucherLines.verificationTypes'),
          value: uniqueValues(verificationRows.map((entry) => entry.entryType)).join(', ')
        },
        { label: t('companyAccountingReport.voucherLines.transaction'), value: row.transactionId },
        {
          label: t('companyAccountingReport.voucherLines.adyenReference'),
          value: uniqueValues(verificationRows.map((entry) => entry.adyenReference)).join(', ')
        },
        {
          label: t('companyAccountingReport.voucherLines.trackingKeys'),
          value: uniqueValues(verificationRows.map((entry) => entry.sourceEventKey)).join(', ')
        }
      ]
    });
  };

  const openPaymentVoucher = (row: CompanyAccountingPaymentRow) => {
    if (!report) return;
    setVoucher({
      kind: 'payment',
      reference: row.pspReference || row.paymentId,
      date: row.date,
      project: projectReference(t, row),
      contract: contractReference(t, row),
      milestone: milestoneReference(t, row),
      customer: row.customerName || '—',
      company: companyLabel,
      currency: row.currency,
      amountOre: row.netToCompanyOre,
      lines: [
        { label: t('companyAccountingReport.voucherLines.paymentId'), value: row.paymentId },
        { label: t('companyAccountingReport.voucherLines.pspReference'), value: row.pspReference },
        { label: t('companyAccountingReport.voucherLines.status'), value: row.status },
        {
          label: t('companyAccountingReport.voucherLines.buyerPrincipalNote'),
          value: null,
          amountOre: row.grossAmountOre,
          semantic: 'position'
        },
        {
          label: t('companyAccountingReport.voucherLines.etappioFeeSellerCost'),
          value: null,
          amountOre: row.etappioFeeOre,
          semantic: 'cost'
        },
        {
          label: t('companyAccountingReport.voucherLines.paymentMethodFee'),
          value: null,
          amountOre: row.paymentMethodFeeOre,
          semantic: 'cost'
        },
        {
          label: t('companyAccountingReport.voucherLines.confirmedAdyenFee'),
          value: null,
          amountOre: row.adyenFeeOre,
          semantic: 'cost'
        },
        {
          label: t('companyAccountingReport.voucherLines.netLiquidityAdyen'),
          value: null,
          amountOre: row.netToCompanyOre,
          semantic: 'position'
        }
      ]
    });
  };

  const openRefundVoucher = (row: CompanyAccountingRefundRow) => {
    if (!report) return;
    setVoucher({
      kind: row.kind,
      reference: row.reference,
      date: row.date,
      project: projectReference(t, row),
      contract: contractReference(t, row),
      milestone: milestoneReference(t, row),
      customer: row.customerName || '—',
      company: companyLabel,
      currency: row.currency,
      amountOre: row.companyBalanceAdjustmentOre,
      lines: [
        { label: t('companyAccountingReport.voucherLines.paymentId'), value: row.paymentId },
        { label: t('companyAccountingReport.voucherLines.originalPspReference'), value: row.originalPspReference },
        {
          label: t('companyAccountingReport.voucherLines.refundPspReference'),
          value: row.refundPspReference
        },
        {
          label: t('companyAccountingReport.voucherLines.status'),
          value:
            row.kind === 'refund'
              ? t('companyAccountingReport.voucherValues.refundCompleted')
              : t('companyAccountingReport.voucherValues.refundReversed')
        },
        {
          label: t('companyAccountingReport.voucherLines.principalAdjustment'),
          value: null,
          amountOre: row.grossRevenueAdjustmentOre,
          semantic: 'position'
        },
        {
          label: t('companyAccountingReport.voucherLines.etappioFeeCorrection'),
          value: null,
          amountOre: row.etappioFeeAdjustmentOre,
          semantic: 'cost'
        },
        {
          label: t('companyAccountingReport.voucherLines.paymentMethodFeeCorrection'),
          value: null,
          amountOre: row.paymentMethodFeeAdjustmentOre,
          semantic: 'cost'
        },
        {
          label: t('companyAccountingReport.voucherLines.adyenFeeCorrection'),
          value: null,
          amountOre: row.adyenFeeAdjustmentOre,
          semantic: 'cost'
        },
        {
          label: t('companyAccountingReport.voucherLines.adyenBalanceChange'),
          value: null,
          amountOre: row.companyBalanceAdjustmentOre,
          semantic: 'position'
        }
      ]
    });
  };

  const openPayoutVoucher = (row: CompanyAccountingPayoutRow) => {
    if (!report) return;
    setVoucher({
      kind: 'payout',
      reference: row.transferReference,
      date: row.date,
      project: projectReference(t, row),
      contract: contractReference(t, row),
      milestone: milestoneReference(t, row),
      customer: row.customerName || '—',
      company: companyLabel,
      currency: row.currency,
      amountOre: row.amountOre,
      lines: [
        { label: t('companyAccountingReport.voucherLines.payoutId'), value: row.payoutId },
        { label: t('companyAccountingReport.voucherLines.paymentId'), value: row.paymentId },
        { label: t('companyAccountingReport.voucherLines.transferReference'), value: row.transferReference },
        { label: t('companyAccountingReport.voucherLines.pspReference'), value: row.pspReference },
        { label: t('companyAccountingReport.voucherLines.statementDescriptor'), value: row.statementDescriptor },
        { label: t('companyAccountingReport.voucherLines.status'), value: row.status },
        { label: t('companyAccountingReport.voucherLines.paidOutAmount'), value: null, amountOre: row.amountOre }
      ]
    });
  };

  const openLedgerVoucher = (row: CompanyAccountingLedgerRow) => {
    if (!report) return;
    setVoucher({
      kind: 'ledger',
      reference: row.accountingReference || row.reference || row.id,
      date: row.date,
      project: t('companyAccountingReport.references.unclassifiedLedgerRow'),
      customer: '—',
      company: companyLabel,
      currency: row.currency,
      amountOre: row.amountOre,
      lines: [
        { label: t('companyAccountingReport.voucherLines.ledgerId'), value: row.id },
        { label: t('companyAccountingReport.voucherLines.valueDate'), value: formatDate(row.valueDate) },
        { label: t('companyAccountingReport.voucherLines.classification'), value: row.classification },
        { label: t('companyAccountingReport.voucherLines.direction'), value: row.direction },
        { label: t('companyAccountingReport.voucherLines.adyenBalanceAccount'), value: row.balanceAccountId },
        { label: t('companyAccountingReport.voucherLines.adyenReferenceRaw'), value: row.reference },
        { label: t('companyAccountingReport.voucherLines.pspReference'), value: row.pspReference },
        { label: t('companyAccountingReport.voucherLines.transferId'), value: row.transferId },
        { label: t('companyAccountingReport.voucherLines.amount'), value: null, amountOre: row.amountOre }
      ]
    });
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('companyAccountingReport.title')}</h5>
          </div>
          <Breadcrumb
            items={[
              { label: t('companyAccountingReport.breadcrumb.home'), path: '/home' },
              { label: t('companyAccountingReport.breadcrumb.current') }
            ]}
          />
        </div>
        <div className="page-header-right ms-auto d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={loadReport} disabled={isLoading}>
            <i className="feather-refresh-cw me-2"></i>
            {t('companyAccountingReport.actions.refresh')}
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() =>
              printElementById('company-accounting-report-print', t('companyAccountingReport.title'))
            }
            disabled={!report}
          >
            <i className="feather-printer me-2"></i>
            {t('companyAccountingReport.actions.printReport', 'Skriv ut')}
          </button>
          <button className="btn btn-primary" onClick={handleExport} disabled={isExporting || !report}>
            <i className="feather-download me-2"></i>
            {t('companyAccountingReport.actions.exportCsv')}
          </button>
        </div>
      </div>

      <Card className="mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('companyAccountingReport.filters.from')}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted mb-1">{t('companyAccountingReport.filters.to')}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          {report && (
            <div className="col-md-6 text-md-end small text-muted">
              <div>
                {report.company.name} ·{' '}
                {t('companyAccountingReport.meta.orgNr', { number: report.company.organizationNumber || '—' })}
              </div>
              <div>
                {t('companyAccountingReport.meta.generated', { date: formatDate(report.generatedAt) })} ·{' '}
                {report.currency || t('companyAccountingReport.meta.currencyAmbiguous')}
              </div>
            </div>
          )}
        </div>
      </Card>

      {error && <div className="alert alert-danger">{error}</div>}
      {isLoading && <div className="alert alert-info">{t('companyAccountingReport.status.loading')}</div>}

      {report && (
        <div id="company-accounting-report-print">
          {report.completeness.warnings.length > 0 && (
            <div className="alert alert-warning">
              {report.completeness.warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </div>
          )}

          <div className="row g-3 mb-4">
            <SummaryCard
              label={t('companyAccountingReport.summary.recognizedRevenue')}
              value={formatOre(report.totals.recognizedRevenueOre, report.currency)}
              tone="success"
            />
            <SummaryCard
              label={t('companyAccountingReport.summary.sellerCosts')}
              value={formatOre(report.totals.sellerCostsOre, report.currency)}
              tone="danger"
            />
            <SummaryCard
              label={t('companyAccountingReport.summary.buyerLiability')}
              value={formatOre(report.totals.buyerPrincipalLiabilityOre, report.currency)}
              tone="warning"
            />
            <SummaryCard
              label={t('companyAccountingReport.summary.buyerLiabilityChange')}
              value={formatOre(report.totals.buyerPrincipalLiabilityChangeOre, report.currency)}
              tone="primary"
            />
          </div>

          <Card className="mb-4" data-testid="rot-reconciliation">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
              <div>
                <h6 className="fw-bold mb-1">{t('companyAccountingReport.sections.rot.heading')}</h6>
                <div className="small text-muted">{t('companyAccountingReport.sections.rot.description')}</div>
              </div>
              <span className="badge bg-info text-dark">{t('companyAccountingReport.sections.rot.badge')}</span>
            </div>
            <RotReconciliationTable rows={report.rotReconciliation} currency={report.currency} />
          </Card>

          <Card className="mb-4">
            <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-3">
              <div>
                <h6 className="fw-bold mb-1">{t('companyAccountingReport.sections.accounting.heading')}</h6>
                <div className="small text-muted">
                  {t('companyAccountingReport.sections.accounting.description')}
                </div>
              </div>
              <div className="d-flex align-items-end flex-wrap gap-3">
                <div>
                  <label className="form-label small text-muted mb-1" htmlFor="accounting-project-filter">
                    {t('companyAccountingReport.filters.projectLabel')}
                  </label>
                  <select
                    id="accounting-project-filter"
                    className="form-select form-select-sm"
                    value={selectedProjectId}
                    onChange={(event) => setSelectedProjectId(event.target.value)}
                    style={{ minWidth: 260 }}
                  >
                    <option value="">{t('companyAccountingReport.filters.allProjects')}</option>
                    {report.projects.map((project) => (
                      <option value={project.id} key={project.id}>
                        {t('companyAccountingReport.references.projectWithDescription', {
                          number: project.projectNumber,
                          description: project.description
                        })}
                      </option>
                    ))}
                    {report.accountingEntries.some((entry) => !entry.projectId) && (
                      <option value="__without_project__">
                        {t('companyAccountingReport.filters.withoutProject')}
                      </option>
                    )}
                  </select>
                </div>
                <div className="form-check form-switch mb-1">
                  <input
                    id="accounting-project-grouping"
                    className="form-check-input"
                    type="checkbox"
                    checked={groupAccountingByProject}
                    onChange={(event) => setGroupAccountingByProject(event.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="accounting-project-grouping">
                    {t('companyAccountingReport.filters.groupByProject')}
                  </label>
                </div>
                <span className="badge bg-light text-dark mb-1">
                  {t('companyAccountingReport.meta.rowCount', {
                    filtered: filteredAccountingEntries.length,
                    total: report.accountingEntries.length
                  })}
                </span>
              </div>
            </div>
            <AccountingEntryTable
              rows={filteredAccountingEntries}
              currency={report.currency}
              onVoucher={openAccountingVoucher}
              groupByProject={groupAccountingByProject}
            />
          </Card>

          <Card className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold mb-1">{t('companyAccountingReport.sections.liability.heading')}</h6>
                <div className="small text-muted">
                  {t('companyAccountingReport.sections.liability.description')}
                </div>
              </div>
              <span className="badge bg-light text-dark">{report.buyerPrincipalLiabilities.length}</span>
            </div>
            <BuyerPrincipalLiabilityTable rows={report.buyerPrincipalLiabilities} />
          </Card>

          <Card className="mb-4">
            <div className="d-flex justify-content-between flex-wrap gap-3 mb-3">
              <div>
                <h6 className="fw-bold mb-1">{t('companyAccountingReport.sections.reconciliation.heading')}</h6>
                <div className="small text-muted font-monospace">{report.reportId}</div>
              </div>
              <div className="text-end small text-muted">
                <div>
                  {t('companyAccountingReport.sections.reconciliation.ledgerIn', {
                    amount: formatOre(report.totals.ledgerIncomingOre, report.currency)
                  })}
                </div>
                <div>
                  {t('companyAccountingReport.sections.reconciliation.ledgerOut', {
                    amount: formatOre(report.totals.ledgerOutgoingOre, report.currency)
                  })}
                </div>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-md-3">
                <div className="text-muted small">
                  {t('companyAccountingReport.sections.reconciliation.paidOut')}
                </div>
                <div className="fw-semibold">{formatOre(report.totals.totalPayoutOre, report.currency)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">
                  {t('companyAccountingReport.sections.reconciliation.expectedPending')}
                </div>
                <div className="fw-semibold">
                  {formatOre(report.reconciliation.expectedPendingPayoutOre, report.currency)}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">
                  {t('companyAccountingReport.sections.reconciliation.actualPending')}
                </div>
                <div className="fw-semibold">
                  {formatOre(report.reconciliation.actualPendingPayoutOre, report.currency)}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted small">
                  {t('companyAccountingReport.sections.reconciliation.postRefundClaim')}
                </div>
                <div className="fw-semibold text-danger">
                  {formatOre(report.totals.postPayoutRefundClaimOre, report.currency)}
                </div>
              </div>
            </div>
          </Card>

          <Card className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold mb-0">{t('companyAccountingReport.sections.payments.heading')}</h6>
                <div className="small text-muted">
                  {t('companyAccountingReport.sections.payments.description')}
                </div>
              </div>
              <span className="badge bg-light text-dark">{report.payments.length}</span>
            </div>
            <PaymentTable rows={report.payments} onVoucher={openPaymentVoucher} />
          </Card>

          <Card className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">{t('companyAccountingReport.sections.refunds.heading')}</h6>
              <span className="badge bg-light text-dark">{report.refunds.length}</span>
            </div>
            <RefundTable rows={report.refunds} onVoucher={openRefundVoucher} />
          </Card>

          <Card className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">{t('companyAccountingReport.sections.payouts.heading')}</h6>
              <span className="badge bg-light text-dark">{report.payouts.length}</span>
            </div>
            <PayoutTable rows={report.payouts} onVoucher={openPayoutVoucher} />
          </Card>

          <Card>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">{t('companyAccountingReport.sections.ledger.heading')}</h6>
              <span className="badge bg-warning text-dark">{report.unknownLedgerRows.length}</span>
            </div>
            <UnknownLedgerTable rows={report.unknownLedgerRows} onVoucher={openLedgerVoucher} />
          </Card>
        </div>
      )}
      {voucher && <VoucherModal voucher={voucher} onClose={() => setVoucher(null)} />}
    </DashboardLayout>
  );
}
